import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Header } from "@/components/Header";
import { adminGetBookings, adminSendInvoiceBulk } from "@/lib/api";
import { toast } from "sonner";
import { Printer, ArrowLeft, Send, Snowflake } from "lucide-react";
import { parseISODate } from "@/lib/format";

const TOKEN_KEY = "ice_admin_token";
const PRICE_PER_CHILD = 14;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_group-ice-sessions/artifacts/hnppn2we_Sligo%20On%20Ice%20Logo%20-%20Small%20Image.png";
const WEEKDAY_LONG = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const CONTACT_EMAIL = "sligoonice@gmail.com";

const humanDate = (iso) => {
  if (!iso) return "";
  const d = parseISODate(iso);
  return `${WEEKDAY_LONG[(d.getDay() + 6) % 7]}, ${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

const formatEuro = (n) => `€${Number(n || 0).toLocaleString("en-IE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const invoiceNumber = (bookings) => {
  if (!bookings || bookings.length === 0) return "";
  // Prefer the persisted invoice_number if any booking has one (all should share it)
  const assigned = bookings.find((b) => b.invoice_number)?.invoice_number;
  if (assigned) return assigned;
  if (bookings.length === 1) return `INV-${bookings[0].reference?.replace("SOI-", "") || ""}`;
  const first = bookings[0].reference?.replace("SOI-", "") || "";
  return `INV-${first}-M${bookings.length}`;
};

const issuedDate = () => {
  const d = new Date();
  return `${WEEKDAY_LONG[(d.getDay() + 6) % 7]}, ${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

export default function AdminInvoicePage() {
  const { id } = useParams(); // id may be a single id or comma-separated list of ids
  const [token] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const idList = useMemo(() => (id ? id.split(",").filter(Boolean) : []), [id]);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const bs = await adminGetBookings(token);
        const matched = idList
          .map((x) => bs.find((b) => b.id === x))
          .filter(Boolean);
        setBookings(matched);
      } catch (e) {
        toast.error("Failed to load booking(s)");
      } finally {
        setLoading(false);
      }
    })();
  }, [token, idList]);

  const sameSchool = useMemo(() => {
    if (bookings.length <= 1) return true;
    const first = (bookings[0].school_name || "").trim().toLowerCase();
    return bookings.every((b) => (b.school_name || "").trim().toLowerCase() === first);
  }, [bookings]);

  const primary = bookings[0];

  const anyPoNumber = useMemo(() => {
    const found = bookings.find((b) => b.po_number);
    return found ? found.po_number : "";
  }, [bookings]);

  const primaryRecipient = useMemo(() => {
    // For emailing: use first booking's email
    return primary?.email || "";
  }, [primary]);

  const subtotal = useMemo(
    () => bookings.reduce((sum, b) => sum + (b.group_size || 0) * PRICE_PER_CHILD, 0),
    [bookings]
  );
  const vat = 0;
  const total = subtotal + vat;

  const sendEmail = async () => {
    if (bookings.length === 0) return;
    if (!window.confirm(`Email invoice ${invoiceNumber(bookings)} to ${primaryRecipient}?`)) return;
    try {
      setSending(true);
      const res = await adminSendInvoiceBulk(token, idList);
      toast.success(`Invoice emailed (id ${res.email_id?.slice(-6) || "ok"})`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to send invoice";
      toast.error(typeof msg === "string" ? msg : "Failed to send invoice");
    } finally {
      setSending(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="max-w-md mx-auto px-4 py-20 text-center">
          <p className="font-mono-tight text-sm text-slate-600 mb-4">Admin sign-in required.</p>
          <Link to="/admin" className="bg-sky-500 text-white font-display uppercase tracking-wide border-2 border-slate-900 brutal-shadow brutal-hover px-6 py-3 inline-block">
            Go to admin login
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="p-16 text-center font-mono-tight uppercase tracking-widest">Loading…</div>
      </div>
    );
  }

  if (bookings.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="max-w-md mx-auto px-4 py-20 text-center">
          <p className="font-mono-tight text-sm text-slate-600 mb-4">No matching bookings found.</p>
          <Link to="/admin" className="bg-sky-500 text-white font-display uppercase tracking-wide border-2 border-slate-900 brutal-shadow brutal-hover px-6 py-3 inline-block">
            Back to admin
          </Link>
        </div>
      </div>
    );
  }

  // Sort bookings by date + time for line items
  const sortedBookings = [...bookings].sort((a, b) => {
    if (a.date !== b.date) return a.date.localeCompare(b.date);
    return (a.time || "").localeCompare(b.time || "");
  });

  const uniqueSchools = Array.from(new Set(bookings.map((b) => b.school_name).filter(Boolean)));
  const isPaid = bookings.length > 0 && bookings.every((b) => !!b.invoice_paid_at);

  return (
    <div className="min-h-screen bg-slate-50 print-clean">
      <div className="no-print"><Header /></div>

      <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5 no-print">
          <Link
            to="/admin"
            className="bg-white text-slate-900 font-mono-tight text-xs uppercase tracking-widest border-2 border-slate-900 brutal-shadow brutal-hover px-3 py-2 flex items-center gap-2"
            data-testid="invoice-back"
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2.5} /> Admin
          </Link>
          <div className="flex items-center gap-2">
            <button
              onClick={sendEmail}
              disabled={sending}
              className="bg-white text-slate-900 font-mono-tight text-xs uppercase tracking-widest border-2 border-slate-900 brutal-shadow brutal-hover px-3 py-2 flex items-center gap-2 disabled:opacity-60"
              data-testid="invoice-email"
            >
              <Send className="w-3.5 h-3.5" strokeWidth={2.5} /> {sending ? "Sending…" : "Email invoice"}
            </button>
            <button
              onClick={() => window.print()}
              className="bg-sky-500 text-white font-display uppercase tracking-wide border-2 border-slate-900 brutal-shadow brutal-hover px-4 py-2 flex items-center gap-2"
              data-testid="invoice-print"
            >
              <Printer className="w-4 h-4" strokeWidth={2.5} /> Print / Save PDF
            </button>
          </div>
        </div>

        {/* Invoice sheet */}
        <div className="bg-white border-2 border-slate-900 brutal-shadow print:border-0 print:shadow-none relative" data-testid="invoice-sheet">
          {isPaid && (
            <div
              className="absolute top-40 right-16 border-4 border-emerald-600 text-emerald-600 font-display uppercase tracking-widest text-4xl px-8 py-2 rotate-[-12deg] opacity-90 pointer-events-none z-10"
              data-testid="invoice-paid-stamp"
            >
              Paid
            </div>
          )}
          {/* Header */}
          <div className="flex items-start justify-between p-8 border-b-2 border-slate-900 print:p-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 border-2 border-slate-900 overflow-hidden bg-slate-900">
                <img src={LOGO_URL} alt="Sligo On Ice" className="w-full h-full object-cover" />
              </div>
              <div>
                <div className="font-display text-2xl uppercase leading-none">Sligo On Ice</div>
                <div className="font-mono-tight text-[10px] uppercase tracking-[0.3em] text-slate-500 mt-1">
                  Rink Booking 2026/27
                </div>
                <div className="font-mono-tight text-xs text-slate-600 mt-3" data-testid="invoice-contact-email">
                  {CONTACT_EMAIL}
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="inline-block bg-slate-900 text-white px-4 py-1 font-display uppercase text-lg">
                Invoice
              </div>
              <div className="font-mono-tight text-xs text-slate-500 mt-3 uppercase tracking-widest">Invoice No</div>
              <div className="font-mono-tight text-sm font-bold" data-testid="invoice-number">{invoiceNumber(bookings)}</div>
              <div className="font-mono-tight text-xs text-slate-500 mt-3 uppercase tracking-widest">Issued</div>
              <div className="font-mono-tight text-sm">{issuedDate()}</div>
              {anyPoNumber && (
                <>
                  <div className="font-mono-tight text-xs text-slate-500 mt-3 uppercase tracking-widest">PO Number</div>
                  <div className="font-mono-tight text-sm font-bold" data-testid="invoice-po-number">{anyPoNumber}</div>
                </>
              )}
            </div>
          </div>

          {/* Bill to + Session summary */}
          <div className="grid md:grid-cols-2 gap-0 border-b-2 border-slate-900">
            <div className="p-8 border-r-2 border-slate-900 print:p-6" data-testid="invoice-bill-to">
              <div className="font-mono-tight text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-3">Bill to</div>
              {sameSchool ? (
                <>
                  <div className="font-bold text-lg text-slate-900">{primary.school_name}</div>
                  <div className="text-sm text-slate-700 mt-1 whitespace-pre-line">{primary.school_address}</div>
                  <div className="text-sm text-slate-700">{primary.county}, Ireland</div>
                </>
              ) : (
                <>
                  <div className="font-mono-tight text-[10px] uppercase tracking-widest text-slate-500 mb-2">Schools</div>
                  <ul className="space-y-1">
                    {uniqueSchools.map((s) => (
                      <li key={s} className="font-bold text-slate-900">{s}</li>
                    ))}
                  </ul>
                </>
              )}
            </div>
            <div className="p-8 print:p-6" data-testid="invoice-summary">
              <div className="font-mono-tight text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-3">
                {sortedBookings.length === 1 ? "Session" : `${sortedBookings.length} sessions`}
              </div>
              {sortedBookings.length === 1 ? (
                <>
                  <div className="font-bold text-lg text-slate-900">{humanDate(sortedBookings[0].date)}</div>
                  <div className="font-mono-tight text-2xl text-sky-600 font-bold mt-1">
                    {sortedBookings[0].time} <span className="text-sm text-slate-500">(45 min)</span>
                  </div>
                  <div className="border-t border-slate-200 mt-4 pt-3 text-sm text-slate-700">
                    <div>
                      <span className="font-mono-tight text-xs text-slate-500 uppercase tracking-widest">Reference</span>
                      {" · "}<span className="font-mono-tight font-bold">{sortedBookings[0].reference}</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-sm text-slate-700 space-y-1">
                  <div className="font-mono-tight text-xs text-slate-500 uppercase tracking-widest mb-2">References</div>
                  {sortedBookings.map((b) => (
                    <div key={b.id} className="font-mono-tight text-xs">{b.reference}</div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Line items */}
          <table className="w-full">
            <thead className="bg-slate-900 text-white font-mono-tight uppercase text-[11px] tracking-widest print:bg-slate-100 print:text-slate-900">
              <tr>
                <th className="text-left px-6 py-3">Description</th>
                <th className="text-right px-6 py-3 w-24">Qty</th>
                <th className="text-right px-6 py-3 w-32">Rate</th>
                <th className="text-right px-6 py-3 w-32">Amount</th>
              </tr>
            </thead>
            <tbody>
              {sortedBookings.map((b) => (
                <tr key={b.id} className="border-t-2 border-slate-900" data-testid={`invoice-line-item-${b.reference}`}>
                  <td className="px-6 py-5">
                    <div className="font-bold text-slate-900">{b.teacher_name}</div>
                    <div className="text-sm text-slate-600 mt-1">
                      {humanDate(b.date)} at {b.time}
                      {!sameSchool && b.school_name ? ` · ${b.school_name}` : ""}
                    </div>
                    <div className="text-xs text-slate-500 font-mono-tight mt-1">
                      Ref {b.reference}
                    </div>
                  </td>
                  <td className="px-6 py-5 text-right font-mono-tight font-bold text-lg">{b.group_size}</td>
                  <td className="px-6 py-5 text-right font-mono-tight">{formatEuro(PRICE_PER_CHILD)}</td>
                  <td className="px-6 py-5 text-right font-mono-tight font-bold" data-testid={`invoice-line-amount-${b.reference}`}>
                    {formatEuro((b.group_size || 0) * PRICE_PER_CHILD)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals — full width now (no payment terms / notes column) */}
          <div className="flex justify-end border-t-2 border-slate-900">
            <table className="w-full md:w-1/2 font-mono-tight">
              <tbody>
                <tr>
                  <td className="px-8 py-3 uppercase text-xs text-slate-500 tracking-widest">Subtotal</td>
                  <td className="px-8 py-3 text-right" data-testid="invoice-subtotal">{formatEuro(subtotal)}</td>
                </tr>
                <tr>
                  <td className="px-8 py-3 uppercase text-xs text-slate-500 tracking-widest">VAT (0%)</td>
                  <td className="px-8 py-3 text-right" data-testid="invoice-vat">{formatEuro(vat)}</td>
                </tr>
                <tr className="bg-sky-500 text-white">
                  <td className="px-8 py-4 uppercase text-sm tracking-widest font-bold">Total due</td>
                  <td className="px-8 py-4 text-right text-2xl font-bold" data-testid="invoice-total">{formatEuro(total)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Company details footer */}
          <div className="border-t-2 border-slate-900 p-8 print:p-6 bg-slate-50" data-testid="invoice-company-footer">
            <div className="font-mono-tight text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-3">Payable to</div>
            <div className="grid md:grid-cols-2 gap-6 text-sm text-slate-800">
              <div className="space-y-1">
                <div className="font-bold text-slate-900">Profile Music Group Ltd</div>
                <div>Unit 6, Tenure Business Park</div>
                <div>Tenure, Co. Louth, A92 NF29</div>
                <div className="pt-2 font-mono-tight text-xs text-slate-600">{CONTACT_EMAIL}</div>
                <div className="font-mono-tight text-xs text-slate-600">Company No. 487637 · VAT IE9757257P</div>
              </div>
              <div className="space-y-1">
                <div className="font-mono-tight text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Bank details</div>
                <div><span className="text-slate-500">Bank:</span> Bank of Ireland, Dunboyne, Co. Meath</div>
                <div className="font-mono-tight text-xs"><span className="text-slate-500">IBAN:</span> IE28BOFI90334995505369</div>
                <div className="font-mono-tight text-xs"><span className="text-slate-500">BIC:</span> BOFIIE2DXXX</div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 border-t-2 border-slate-900 bg-slate-900 text-white flex items-center justify-between text-[10px] font-mono-tight uppercase tracking-widest print:bg-slate-100 print:text-slate-900">
            <span className="flex items-center gap-2"><Snowflake className="w-3 h-3" strokeWidth={3} /> Sligo On Ice · 2026/27</span>
            <span>Thank you for your booking</span>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 10mm; }
          body { background: #fff !important; }
          .no-print { display: none !important; }
          .print-clean { background: #fff !important; }
          [data-testid="invoice-sheet"] { box-shadow: none !important; }
        }
      `}</style>
    </div>
  );
}
